import { RobotoMarkdown } from "@/shared/components/RobotoMarkdown";
import type { SkillAccessibility } from "@/shared/domain/skills";
import {
  ConfirmationCard,
  ConfirmationField,
  ConfirmationFields,
  ConfirmationLead,
  ConfirmationNote,
  ConfirmationPanel,
  ConfirmationTags,
} from "@/shared/foundation/primitives";

import type { ConfirmationContentProps } from "./ClientTools";
import { coerceToSkillDraft, type CreateSkillInput } from "./createSkillCoerce";
import styles from "./CreateSkillConfirmation.module.css";

const VISIBILITY_LABELS: Record<SkillAccessibility, string> = {
  private: "Private",
  org: "Org",
  "org-editable": "Org-editable",
};

/**
 * Read-only preview of what the model wants to open the Skills editor with.
 * Runs the proposal through the same {@link coerceToSkillDraft} the tool's
 * `execute` uses, so what is shown matches what the editor opens with.
 *
 * The two modes preview different things, because they change different
 * things. A **create** shows the name, body, deduped tags, and resolved
 * visibility — everything the new skill will be made from. An **update**
 * replaces version *content* only, so it names the targeted skill and shows
 * the proposed description / body; the name and visibility chip are omitted
 * because an update can change neither (name and accessibility belong to the
 * skill, and are edited from the Skills list, not this editor).
 *
 * The editor — not approval — is the validation seam now, so an incomplete
 * proposal still previews what it can (and the omitted fields simply don't
 * render); the user fills the rest in the editor. The only "nothing to show"
 * case is a proposal with no name, description, *and* body, where the draft
 * coerces to null and approving would error rather than open a blank form.
 */
export function CreateSkillConfirmation({
  params,
}: ConfirmationContentProps<CreateSkillInput>) {
  const draft = coerceToSkillDraft(params);

  if (draft === null) {
    return (
      <ConfirmationPanel label="Create skill preview">
        <ConfirmationLead>
          There&rsquo;s nothing to open the editor with yet — the draft needs at
          least a name, a description, or a procedure.
        </ConfirmationLead>
      </ConfirmationPanel>
    );
  }

  const {
    name,
    description,
    body,
    accessibility,
    targetSkillName,
    tags = [],
    removeTags = [],
  } = draft;
  const isUpdate = targetSkillName !== undefined;

  return (
    <ConfirmationPanel label="Create skill preview">
      <ConfirmationLead>
        {isUpdate
          ? "Open this skill in the editor with the proposed content?"
          : "Open the Skills editor with this draft?"}
      </ConfirmationLead>
      <ConfirmationCard
        // "Create X" / "Update X" rather than a bare name: one card serves
        // both modes, so the heading is what tells the user which they're
        // approving. Matches the sibling `CreateAgentConfirmation`.
        title={
          isUpdate
            ? `Update ${targetSkillName}`
            : `Create ${name ?? "(unnamed)"}`
        }
        // Visibility is a create-time choice — it isn't touched on an update
        // (accessibility lives on the skill, not the version), so only the
        // create preview shows the tier badge.
        badge={
          !isUpdate && accessibility
            ? VISIBILITY_LABELS[accessibility]
            : undefined
        }
      >
        <ConfirmationFields>
          {/* An update carries no rename, unlike the agent tool. Say so when
              the model proposed a different name, rather than dropping it in
              silence — otherwise the user reads the card as agreeing to it. */}
          {isUpdate && name !== undefined && name !== targetSkillName && (
            <ConfirmationField label="Ignored">
              Proposed name &ldquo;{name}&rdquo; — an update replaces content
              only, so the skill keeps its current name.
            </ConfirmationField>
          )}
          {description && (
            <ConfirmationField label="When to use">
              {description}
            </ConfirmationField>
          )}
          {body && (
            <ConfirmationField label="Procedure">
              <div className={styles.body}>
                <RobotoMarkdown content={body} variant="compact" />
              </div>
            </ConfirmationField>
          )}
          {tags.length > 0 && (
            // "Tags to add" on an update: they go out as `put_tags`, which
            // merges rather than replaces, so labelling them "Tags" would read
            // as the skill's whole tag set after approval.
            <ConfirmationField label={isUpdate ? "Tags to add" : "Tags"}>
              <ConfirmationTags tags={tags} />
            </ConfirmationField>
          )}
          {/* Its own field rather than a marking on the added ones: a deletion
              is the part of the proposal worth reading twice, and the label is
              what says so. `coerceToSkillDraft` drops these on a create, so
              this only renders on an update. */}
          {removeTags.length > 0 && (
            <ConfirmationField label="Tags to remove">
              <ConfirmationTags tags={removeTags} />
            </ConfirmationField>
          )}
        </ConfirmationFields>
      </ConfirmationCard>
      <ConfirmationNote>
        {isUpdate
          ? "Approving opens the existing skill in the editor with this draft prefilled — nothing is saved until you review it and click Save there."
          : "Approving opens the Skills editor pre-filled with this draft — the skill isn’t created until you review it and click Save there."}
      </ConfirmationNote>
    </ConfirmationPanel>
  );
}
